/**
 * A table the model wrote, as a person edits it (DOP-637): cell texts under
 * column headings, rows and columns added or taken out.
 *
 * What is saved is the whole table as it was left, in a `replace` op over
 * the generated one. The server keeps the source of every cell whose text
 * it still finds and marks the cells the person typed as theirs, so an
 * untouched figure stays cited.
 */

import type {
	DealScreeningEditOpTable,
	DealScreeningEditTable,
	DealScreeningTable,
} from "../../types/dealScreening";
import { type DocumentSource, fromSavedSource, sentSource, sourceKey } from "./documentSource";

export type EditTable = DealScreeningEditTable;

export const MAX_ROWS = 60;
export const MAX_COLUMNS = 12;

/** Where a cell's note is kept: "row:column". */
export const cellKey = (row: number, column: number): string => `${row}:${column}`;

export const editTableOf = (table: DealScreeningTable | null | undefined): EditTable | null => {
	if (!table) return null;
	// A person's note on a cell, saved earlier, is its source (DOP-637); so
	// is the document a cell of theirs names (DOP-652). A cell the run wrote
	// keeps its source through the save by its text, and carries neither.
	const notes: Record<string, string> = {};
	const sources: Record<string, DocumentSource> = {};
	table.rows.forEach((row, r) =>
		row.forEach((cell, c) => {
			const document = cell.edited ? fromSavedSource(cell.source) : null;
			if (document) sources[cellKey(r, c)] = document;
			if ((cell.source?.kind === "user" || document) && cell.source?.note) {
				notes[cellKey(r, c)] = cell.source.note;
			}
		}),
	);
	return {
		caption: table.caption,
		columns: [...table.columns],
		rows: table.rows.map((row) => row.map((cell) => cell.text)),
		...(Object.keys(notes).length > 0 ? { notes } : {}),
		...(Object.keys(sources).length > 0 ? { sources } : {}),
	};
};

const clean = (text: string) => text.replace(/\s+/g, " ").trim();

/** A table as a comparable string: whether it changed at all. Empty rows do not count. */
export const tableKey = (table: EditTable | null): string =>
	table
		? JSON.stringify([
				clean(table.caption ?? ""),
				table.columns.some((heading) => clean(heading)) ? table.columns.map(clean) : [],
				table.rows.map((row) => row.map(clean)).filter((row) => row.some(Boolean)),
				Object.entries(table.notes ?? {}).sort(),
				Object.entries(table.sources ?? {})
					.map(([key, source]) => [key, sourceKey(source)])
					.sort(),
			])
		: "";

/** The table's words, for the changes list and for spotting new figures. */
export const tableText = (table: EditTable | null): string =>
	table ? [table.caption ?? "", ...table.columns, ...table.rows.flat()].map(clean).filter(Boolean).join(" ") : "";

const width = (table: EditTable) =>
	Math.max(table.columns.length, ...table.rows.map((row) => row.length), 1);

export const setCell = (table: EditTable, row: number, column: number, text: string): EditTable => {
	const next: EditTable = {
		...table,
		rows: table.rows.map((cells, r) =>
			r === row
				? Array.from({ length: width(table) }, (_, c) => (c === column ? text : (cells[c] ?? "")))
				: cells,
		),
	};
	// A document vouches for the value it was given for (DOP-652): typed over,
	// the cell is the person's own and the document no longer speaks for it.
	const was = table.rows[row]?.[column] ?? "";
	if (table.sources?.[cellKey(row, column)] && clean(was) !== clean(text)) {
		return setCellSource(next, row, column, null);
	}
	return next;
};

/** A heading typed; a table that had none gets a heading row. */
export const setHeading = (table: EditTable, column: number, text: string): EditTable => ({
	...table,
	columns: Array.from({ length: width(table) }, (_, c) =>
		c === column ? text : (table.columns[c] ?? ""),
	),
});

export const setCaption = (table: EditTable, caption: string): EditTable => ({ ...table, caption });

export const addRow = (table: EditTable): EditTable =>
	table.rows.length >= MAX_ROWS
		? table
		: { ...table, rows: [...table.rows, Array.from({ length: width(table) }, () => "")] };

/** What sits on cells, by place, with each place moved as `move` says; null drops it. */
const moved = <T,>(
	notes: Record<string, T> | undefined,
	move: (row: number, column: number) => [number, number] | null,
): Record<string, T> | undefined => {
	if (!notes) return notes;
	const out: Record<string, T> = {};
	Object.entries(notes).forEach(([key, note]) => {
		const [row, column] = key.split(":").map(Number);
		const to = move(row, column);
		if (to) out[cellKey(to[0], to[1])] = note;
	});
	return out;
};

/** A table's notes and documents, moved with their cells. */
const movedNotes = (
	table: EditTable,
	move: (row: number, column: number) => [number, number] | null,
): Pick<EditTable, "notes" | "sources"> => ({
	notes: moved(table.notes, move),
	...(table.sources ? { sources: moved(table.sources, move) } : {}),
});

export const removeRow = (table: EditTable, row: number): EditTable => ({
	...table,
	rows: table.rows.filter((_, r) => r !== row),
	...movedNotes(table, (r, c) => (r === row ? null : [r > row ? r - 1 : r, c])),
});

export const addColumn = (table: EditTable): EditTable =>
	width(table) >= MAX_COLUMNS
		? table
		: {
				...table,
				columns: table.columns.length > 0 ? [...table.columns, ""] : [],
				rows: table.rows.map((row) => [...row, ""]),
			};

export const removeColumn = (table: EditTable, column: number): EditTable => ({
	...table,
	columns: table.columns.filter((_, c) => c !== column),
	rows: table.rows.map((row) => row.filter((_, c) => c !== column)),
	...movedNotes(table, (r, c) => (c === column ? null : [r, c > column ? c - 1 : c])),
});

/** The place `index` moves to when the item at `from` moves to `to`. */
const shifted = (index: number, from: number, to: number): number => {
	if (index === from) return to;
	if (from < to && index > from && index <= to) return index - 1;
	if (to < from && index >= to && index < from) return index + 1;
	return index;
};

/** The row at `from` dragged to `to` (DOP-649), its notes with it. */
export const moveRow = (table: EditTable, from: number, to: number): EditTable => {
	if (from === to || from < 0 || to < 0 || from >= table.rows.length || to >= table.rows.length) return table;
	const rows = [...table.rows];
	const [row] = rows.splice(from, 1);
	rows.splice(to, 0, row);
	return { ...table, rows, ...movedNotes(table, (r, c) => [shifted(r, from, to), c]) };
};

/** The column at `from` dragged to `to` (DOP-649): its heading, its cells and their notes. */
export const moveColumn = (table: EditTable, from: number, to: number): EditTable => {
	const columns = width(table);
	if (from === to || from < 0 || to < 0 || from >= columns || to >= columns) return table;
	const move = <T,>(items: T[], fill: T): T[] => {
		const full = Array.from({ length: columns }, (_, c) => items[c] ?? fill);
		const [item] = full.splice(from, 1);
		full.splice(to, 0, item);
		return full;
	};
	return {
		...table,
		columns: table.columns.length > 0 ? move(table.columns, "") : [],
		rows: table.rows.map((row) => move(row, "")),
		...movedNotes(table, (r, c) => [r, shifted(c, from, to)]),
	};
};

/** A cell's note on where it came from, set or (blank) taken off. */
export const setCellNote = (table: EditTable, row: number, column: number, note: string): EditTable => {
	const notes = { ...(table.notes ?? {}) };
	if (note.trim()) notes[cellKey(row, column)] = note.trim();
	else delete notes[cellKey(row, column)];
	return { ...table, notes };
};

/** The document a cell came from, set or (null) taken off (DOP-652). */
export function setCellSource(
	table: EditTable,
	row: number,
	column: number,
	source: DocumentSource | null,
): EditTable {
	const sources = { ...(table.sources ?? {}) };
	if (source) sources[cellKey(row, column)] = source;
	else delete sources[cellKey(row, column)];
	const { sources: _had, ...rest } = table;
	return Object.keys(sources).length > 0 ? { ...rest, sources } : rest;
}

/**
 * The table as the save sends it: empty rows left out (the server drops
 * them too), each note and document moved with its row, and neither kept on
 * an empty cell.
 */
export function toOpTable(table: EditTable): DealScreeningEditOpTable {
	const kept = table.rows.map((row, r) => ({ row, r })).filter(({ row }) => row.some((cell) => cell.trim()));
	const out: DealScreeningEditOpTable = {
		caption: table.caption,
		columns: table.columns,
		rows: kept.map(({ row }) => row),
	};
	const notes = kept.flatMap(({ row, r }, index) =>
		row.flatMap((cell, c) => {
			const note = table.notes?.[cellKey(r, c)] ?? "";
			const source = table.sources?.[cellKey(r, c)];
			if (!cell.trim() || (!note && !source)) return [];
			return [{ row: index, column: c, note, ...(source ? { source: sentSource(source) } : {}) }];
		}),
	);
	if (notes.length > 0) out.notes = notes;
	return out;
}

/**
 * Whether a cell reads differently from how the table started: what "Show
 * my changes" marks. A cell is compared to the one at the same place, so a
 * removed row marks the rows after it; the save keeps every source anyway.
 */
export const cellChanged = (start: EditTable | null, now: EditTable, row: number, column: number): boolean =>
	clean(start?.rows[row]?.[column] ?? "") !== clean(now.rows[row]?.[column] ?? "");

export const headingChanged = (start: EditTable | null, now: EditTable, column: number): boolean =>
	clean(start?.columns[column] ?? "") !== clean(now.columns[column] ?? "");
